import { Lock } from "lucide-react";

const PAGES = [
  "/catalog-preview/2026-809ae10c/page-05.jpg",
  "/catalog-preview/2026-809ae10c/page-07.jpg",
  "/catalog-preview/2026-809ae10c/page-17.jpg",
  "/catalog-preview/2026-809ae10c/page-19.jpg",
  "/catalog-preview/2026-809ae10c/page-24.jpg",
  "/catalog-preview/2026-809ae10c/page-30.jpg",
];

/**
 * Auto-scrolling catalog tease. Pages glide across a soft charcoal band with
 * gold-tinted edges. Hovering pauses the marquee; a central lock chip nudges
 * visitors to apply below to unlock the full catalog.
 */
export default function CatalogPreview({ onCTAClick }) {
  // Double the pages for a seamless loop via CSS keyframe translate.
  const strip = ["original", "repeat"].flatMap((copy) => PAGES.map((src) => ({ src, id: `${copy}:${src}` })));

  return (
    <section
      className="relative py-16 md:py-24 bg-brand-charcoal text-brand-bone overflow-hidden"
      data-testid="catalog-preview"
    >
      {/* Soft gold radial glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #C39A5A 0%, transparent 50%), radial-gradient(circle at 80% 70%, #C39A5A 0%, transparent 45%)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 text-center mb-10 md:mb-14">
        <span className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold font-medium">
          Inside the Atelier Catalog
        </span>
        <h2 className="mt-3 font-serif font-light text-3xl md:text-4xl lg:text-5xl tracking-[-0.01em]">
          36 pages of designs, finishes,
          <span className="block italic text-brand-gold">hardware and specs.</span>
        </h2>
        <p className="mt-4 text-brand-bone/70 text-sm md:text-base font-light max-w-2xl mx-auto leading-relaxed">
          A private look inside the atelier — every exterior, interior and garage
          collection, with full material palettes and hardware detailing.
        </p>
      </div>

      {/* Marquee strip */}
      <div
        className="relative group"
        data-testid="catalog-preview-marquee"
      >
        {/* Edge gradients */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-brand-charcoal to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-brand-charcoal to-transparent z-10" />

        <div className="cp-track flex gap-6 md:gap-8 will-change-transform">
          {strip.map(({ src, id }, i) => (
            <figure
              key={id}
              className="cp-card relative shrink-0 w-[230px] md:w-[400px] lg:w-[460px] aspect-[8.5/11] bg-brand-bone shadow-[0_20px_50px_-20px_rgba(0,0,0,0.55)] border border-brand-bone/10 overflow-hidden"
              data-testid={`catalog-preview-card-${i}`}
            >
              <img
                src={src}
                alt={`Atelier catalog page preview ${i + 1}`}
                data-testid={`catalog-preview-image-${i}`}
                className="w-full h-full object-contain"
                loading="lazy"
                draggable={false}
              />
              {/* Subtle paper sheen */}
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-black/10 via-transparent to-white/[0.04]" />
            </figure>
          ))}
        </div>

        {/* Centered lock chip */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-20">
          <div className="pointer-events-auto flex flex-col items-center gap-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-charcoal/85 border border-brand-gold/50 backdrop-blur-md text-brand-gold text-[0.62rem] tracking-[0.28em] uppercase font-semibold shadow-lg">
              <Lock size={13} />
              Full catalog locked
            </div>
            {onCTAClick && (
              <button
                type="button"
                onClick={onCTAClick}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-gold text-brand-green text-[0.72rem] tracking-[0.24em] uppercase font-semibold hover:bg-brand-bone transition-colors shadow-xl"
                data-testid="catalog-preview-cta"
              >
                Apply to unlock →
              </button>
            )}
          </div>
        </div>
      </div>

      <p className="relative mt-8 md:mt-10 text-center text-[0.66rem] tracking-[0.28em] uppercase text-brand-bone/50">
        36 Pages · High-Resolution PDF · Instant Download
      </p>

      <style>{`
        [data-testid="catalog-preview"] .cp-track {
          --half-gap: 0.75rem; /* half of gap-6 (1.5rem) on mobile */
          animation: cp-scroll 15s linear infinite;
        }
        @media (min-width: 768px) {
          [data-testid="catalog-preview"] .cp-track {
            --half-gap: 1rem; /* half of gap-8 (2rem) on desktop */
          }
        }
        [data-testid="catalog-preview"] .group:hover .cp-track { animation-play-state: paused; }
        [data-testid="catalog-preview-marquee"]:hover .cp-track { animation-play-state: paused; }
        [data-testid="catalog-preview"] .cp-card {
          transition: transform .5s ease, box-shadow .5s ease;
        }
        [data-testid="catalog-preview"] .cp-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 30px 60px -20px rgba(0,0,0,0.7);
        }
        @keyframes cp-scroll {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(calc(-50% - var(--half-gap)), 0, 0); }
        }
        @media (max-width: 640px) {
          [data-testid="catalog-preview"] .cp-track { animation-duration: 9s; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="catalog-preview"] .cp-track { animation: none; }
        }
      `}</style>
    </section>
  );
}
